/**
 * Chart Display Component
 * Renders astrological chart SVG from ChartSvgEvent
 * Supports both inline SVG (streaming) and URL-based loading (history)
 */

import React, { useState, useCallback, useEffect } from 'react';
import { ChartSvgEvent } from '@/lib/api/chat';
import { Maximize2, Minimize2, X, Star, Loader2 } from 'lucide-react';

interface ChartDisplayProps {
  chart: ChartSvgEvent;
  onClose?: () => void;
}

/**
 * Renders an astrological chart SVG with expand/collapse functionality
 */
export const ChartDisplay: React.FC<ChartDisplayProps> = ({ chart, onClose }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [loadedSvg, setLoadedSvg] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Load SVG from URL if svg_content is empty but svg_url is available
  useEffect(() => {
    if (!chart.svg_content && chart.svg_url && !loadedSvg && !loading) {
      setLoading(true);
      fetch(chart.svg_url)
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.text();
        })
        .then((svg) => {
          setLoadedSvg(svg);
          setLoading(false);
        })
        .catch((err) => {
          console.error('[ChartDisplay] Failed to load SVG from URL:', err);
          setLoading(false);
        });
    }
  }, [chart.svg_content, chart.svg_url, loadedSvg, loading]);

  const handleToggleExpand = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  // Chart type labels in Chinese
  const chartTypeLabels: Record<string, string> = {
    natal: '本命盘',
    transit: '行运盘',
    synastry: '合盘',
    composite: '组合盘',
    return: '回归盘',
  };

  const chartLabel = chartTypeLabels[chart.chart_type] || '星盘';
  const title = chart.chart_title || (chart.subject_name ? `${chart.subject_name} - ${chartLabel}` : chartLabel);

  // Get effective SVG content (inline or loaded from URL)
  const effectiveSvg = chart.svg_content || loadedSvg;

  // Render the SVG inline
  const renderSvg = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-full text-slate-400 gap-2">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Loading chart...</span>
        </div>
      );
    }

    if (!effectiveSvg) {
      return (
        <div className="flex items-center justify-center h-full text-slate-400">
          <span>No chart data</span>
        </div>
      );
    }

    return (
      <div
        className="chart-svg-container"
        dangerouslySetInnerHTML={{ __html: effectiveSvg }}
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      />
    );
  };

  // Expanded fullscreen view
  if (isExpanded) {
    return (
      <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-2xl max-w-[95vw] max-h-[95vh] overflow-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Star className="w-5 h-5 text-amber-500" />
              <span className="font-medium text-slate-800">{title}</span>
              <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded">
                {chart.theme}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleToggleExpand}
                className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-500 hover:text-slate-700"
                title="Exit fullscreen"
              >
                <Minimize2 className="w-5 h-5" />
              </button>
              {onClose && (
                <button
                  onClick={onClose}
                  className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-500 hover:text-slate-700"
                  title="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>
          {/* Chart Content */}
          <div
            className="p-4"
            style={{
              width: Math.min(chart.width + 48, window.innerWidth * 0.9),
              height: Math.min(chart.height + 48, window.innerHeight * 0.85),
            }}
          >
            {renderSvg()}
          </div>
          {/* Metadata footer */}
          {chart.metadata && (
            <div className="border-t border-slate-200 px-4 py-2 bg-slate-50 text-xs text-slate-500">
              {chart.metadata.birth_date && (
                <span className="mr-4">
                  Birth: {chart.metadata.birth_date}
                </span>
              )}
              {chart.metadata.transit_date && (
                <span className="mr-4">
                  Transit: {chart.metadata.transit_date}
                </span>
              )}
              {chart.metadata.birth_location && (
                <span>Location: {chart.metadata.birth_location}</span>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Inline compact view
  return (
    <div className="mt-2 bg-slate-50 border border-slate-200 rounded-lg overflow-hidden">
      {/* Header */}
      <div className="px-3 py-2 bg-slate-100 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Star className="w-4 h-4 text-amber-500" />
          <span className="text-sm font-medium text-slate-700">{title}</span>
        </div>
        <button
          onClick={handleToggleExpand}
          className="p-1 hover:bg-slate-200 rounded text-slate-500 hover:text-slate-700"
          title="Expand"
        >
          <Maximize2 className="w-4 h-4" />
        </button>
      </div>
      {/* Chart Preview - scaled down */}
      <div
        className="p-2 overflow-hidden cursor-pointer"
        onClick={handleToggleExpand}
        style={{
          maxHeight: 300,
        }}
      >
        <div
          style={{
            transform: 'scale(0.5)',
            transformOrigin: 'top left',
            width: chart.width,
            height: chart.height,
          }}
        >
          {renderSvg()}
        </div>
      </div>
      {/* Click hint */}
      <div className="px-3 py-1.5 text-xs text-slate-400 text-center border-t border-slate-200">
        Click to expand
      </div>
    </div>
  );
};

export default ChartDisplay;
